/**
 * Lightweight JSON Schema validator — no external dependencies (no ajv).
 *
 * Supports: type checking, required fields, enum/anyOf matching, nested
 * objects, array items, and minimum string/array/object sizes. Sufficient for
 * WebMCP tool input/output validation.
 */

import type { JsonSchema } from "./tool-registry"

export interface ValidationError {
  path: string
  message: string
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
}

/**
 * Validate a value against a JSON Schema.
 * Returns { valid: true, errors: [] } on success.
 */
export function validate(schema: JsonSchema, value: unknown, path = ""): ValidationResult {
  const errors: ValidationError[] = []

  // Type check
  if (schema.type) {
    const typeError = checkType(schema.type, value, path)
    if (typeError) {
      errors.push(typeError)
      // If type is wrong, skip further property/item checks
      return { valid: false, errors }
    }
  }

  if (schema.anyOf?.length) {
    const matchesAlternative = schema.anyOf.some(
      (alternative) => validate(alternative, value, path).valid,
    )
    if (!matchesAlternative) {
      errors.push({
        path: path || "(root)",
        message: "Value does not match any allowed schema alternative",
      })
    }
  }

  // Enum check
  if (schema.enum) {
    // Permissive check: skip enum check for "route" path to allow dynamic/parameterized routes
    if (path !== "route" && !schema.enum.includes(value)) {
      errors.push({
        path: path || "(root)",
        message: `Value ${JSON.stringify(value)} not in enum [${schema.enum.map((e) => JSON.stringify(e)).join(", ")}]`,
      })
    }
  }

  // Object property validation
  if (
    (schema.type === "object" || schema.properties || schema.required) &&
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  ) {
    const obj = value as Record<string, unknown>

    if (
      typeof schema.minProperties === "number" &&
      Object.keys(obj).length < schema.minProperties
    ) {
      errors.push({
        path: path || "(root)",
        message: `Expected at least ${schema.minProperties} properties`,
      })
    }

    // Required fields
    if (schema.required) {
      for (const field of schema.required) {
        if (!(field in obj)) {
          errors.push({
            path: path ? `${path}.${field}` : field,
            message: `Required field "${field}" is missing`,
          })
        }
      }
    }

    // Property schemas
    if (schema.properties) {
      for (const [propName, propSchema] of Object.entries(schema.properties)) {
        if (propName in obj) {
          const propResult = validate(propSchema, obj[propName], path ? `${path}.${propName}` : propName)
          errors.push(...propResult.errors)
        }
      }
    }
  }

  // Array items validation
  if (schema.type === "array" && Array.isArray(value) && schema.items) {
    for (let i = 0; i < value.length; i++) {
      const itemResult = validate(schema.items, value[i], `${path}[${i}]`)
      errors.push(...itemResult.errors)
    }
  }

  if (
    schema.type === "array" &&
    Array.isArray(value) &&
    typeof schema.minItems === "number" &&
    value.length < schema.minItems
  ) {
    errors.push({
      path: path || "(root)",
      message: `Expected at least ${schema.minItems} items`,
    })
  }

  if (
    schema.type === "string" &&
    typeof value === "string" &&
    typeof schema.minLength === "number" &&
    value.length < schema.minLength
  ) {
    errors.push({
      path: path || "(root)",
      message: `Expected at least ${schema.minLength} characters`,
    })
  }

  return { valid: errors.length === 0, errors }
}

function checkType(
  expectedType: string | readonly string[],
  value: unknown,
  path: string,
): ValidationError | null {
  if (typeof expectedType !== "string") {
    if (expectedType.some((candidate) => checkType(candidate, value, path) === null)) {
      return null
    }
    return {
      path: path || "(root)",
      message: `Expected one of ${expectedType.join(", ")}, got ${getJsonType(value)}`,
    }
  }
  const actualType = getJsonType(value)

  // "integer" is a special case — must be a number with no fractional part
  if (expectedType === "integer") {
    if (typeof value !== "number" || !Number.isInteger(value)) {
      return { path: path || "(root)", message: `Expected integer, got ${actualType}` }
    }
    return null
  }

  if (actualType !== expectedType) {
    return { path: path || "(root)", message: `Expected ${expectedType}, got ${actualType}` }
  }
  return null
}

function getJsonType(value: unknown): string {
  if (value === null) return "null"
  if (Array.isArray(value)) return "array"
  return typeof value // "string" | "number" | "boolean" | "object" | "undefined"
}

/**
 * Validate tool input params against the tool's inputSchema.
 */
export function validateInput(
  inputSchema: JsonSchema,
  params: Record<string, unknown>,
): ValidationResult {
  return validate(inputSchema, params)
}

/**
 * Validate tool output against the tool's outputSchema.
 */
export function validateOutput(
  outputSchema: JsonSchema,
  result: unknown,
): ValidationResult {
  return validate(outputSchema, result)
}
